import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getUserRole } from "../utils/roles";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || "RUNTIME_REPLACE_REACT_APP_API_URL";

function normalizeUser(apiUser) {
  if (!apiUser) return null;
  return {
    ...apiUser,
    _id: apiUser.id != null ? String(apiUser.id) : apiUser._id,
    id: apiUser.id,
    role: getUserRole(apiUser),
    first_login: apiUser.first_login === true || apiUser.first_login === 1 || apiUser.first_login === "1",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/user`, {
        method: "GET",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setUser(normalizeUser(data));
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (username, password) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || "Login failed");
    }
    const normalized = normalizeUser(data.user || data);
    setUser(normalized);
    return normalized;
  }, []);

  const dismissFirstLogin = useCallback(async () => {
    setUser((prev) => (prev ? { ...prev, first_login: false } : prev));
    try {
      const res = await fetch(`${API_URL}/user/first-login`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        console.warn("Failed to persist first-login dismissal");
      }
    } catch (e) {
      console.warn("Failed to persist first-login dismissal:", e);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.warn("Logout request failed:", e);
    }
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    dismissFirstLogin,
    refreshUser: fetchMe,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
